/**
 * Auto-recovery from stale lazy-loaded chunks after a redeploy (#1808).
 *
 * Why this exists
 * ───────────────
 * The app code-splits routes with `React.lazy()` (see `App.tsx`), so Vite
 * emits content-hashed chunk files (e.g. `Analytics-Fbi5cOEM.js`). On
 * redeploy every hash rotates. A browser tab still holding the *old*
 * `index.html` asks the server for a chunk filename that no longer exists
 * → 404 → Vite throws "Failed to fetch dynamically imported module" and
 * the top-level `ErrorBoundary` renders its fallback. A single reload
 * pulls the fresh `index.html` (new hashes) and the page works again.
 * Before this module, nothing listened for the failure, so the user had
 * to notice the error screen and reload by hand.
 *
 * Policy — reload once, rate-limited by a cooldown (not a one-shot flag)
 * ─────────────────────────────────────────────────────────────────────
 * On `vite:preloadError` we reload, but only if we have not already
 * reloaded within `RELOAD_COOLDOWN_MS`. The cooldown does two jobs a
 * permanent once-per-tab boolean can't do both of:
 *   1. A genuinely broken deploy (chunk really missing, not just
 *      rehashed) can't loop: the post-reload retry fails again *inside*
 *      the cooldown window, so we do NOT `preventDefault()` and Vite
 *      throws the real error into the `ErrorBoundary` instead of us
 *      reloading forever.
 *   2. A *later* redeploy in a long-lived tab still recovers, because the
 *      earlier reload timestamp has aged out of the window. `dev` tabs
 *      left open across several deploys would otherwise self-heal only
 *      once.
 *
 * Vite's documented contract for this event: call `event.preventDefault()`
 * when you handle it yourself, otherwise Vite rethrows. We preventDefault
 * only on the reload path (job 1 above depends on *not* preventing it on
 * the cooldown path). The event carries the original `Error` on `.payload`.
 */
import { getErrorReporter } from '@/lib/errorReporter'

/** How recently a reload must have happened to suppress another one. */
const RELOAD_COOLDOWN_MS = 10_000

/** sessionStorage key holding the epoch-ms of the last recovery reload.
 * `ottotester.*` matches the codebase's namespaced-key convention
 * (`ottotester.signupPlan`, `ottotester.superadminMode`). */
const STORAGE_KEY = 'ottotester.preloadReloadAt'

/**
 * Vite dispatches `vite:preloadError` as a bare `Event` with the original
 * `Error` attached on a non-standard `.payload` property. Typed locally
 * because Vite's client types don't augment `WindowEventMap` for it.
 */
interface VitePreloadErrorEvent extends Event {
  payload?: unknown
}

/** Options exist only as a test seam — production calls with no args. */
export interface PreloadRecoveryOptions {
  /** Defaults to `window.location.reload()`. */
  reload?: () => void
  /** Defaults to `Date.now`. */
  now?: () => number
  /** Defaults to `RELOAD_COOLDOWN_MS`. */
  cooldownMs?: number
}

/**
 * Pure decision: is enough time since the last reload to auto-reload
 * again? Exported so the cooldown boundary is unit-testable without
 * driving the DOM event.
 */
export function shouldAutoReload(
  nowMs: number,
  lastReloadAtMs: number,
  cooldownMs: number = RELOAD_COOLDOWN_MS,
): boolean {
  return nowMs - lastReloadAtMs >= cooldownMs
}

/** Read the last-reload timestamp; returns null if storage is unavailable
 * (e.g. blocked in a locked-down browser). Null means "can't guarantee no
 * loop", so the caller declines to auto-reload rather than risk one. */
function readLastReloadAt(): number | null {
  try {
    // A corrupted/non-numeric stored value parses to NaN, and
    // `shouldAutoReload(now, NaN)` is always false — which would disable
    // recovery for this tab forever. Treat any non-finite value as 0 (no
    // prior reload) so the page can still self-heal.
    const parsed = Number(sessionStorage.getItem(STORAGE_KEY) ?? 0)
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    return null
  }
}

/** Persist the last-reload timestamp; returns false if storage write
 * fails, in which case the caller must not reload (no persisted guard =
 * possible loop). */
function writeLastReloadAt(nowMs: number): boolean {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(nowMs))
    return true
  } catch {
    return false
  }
}

/**
 * Register the `window` `vite:preloadError` listener. Call exactly once at
 * boot (`main.tsx`), alongside `initErrorReporter()`. Registering twice
 * would double-handle the event. Returns a disposer that removes the
 * listener (used by tests; `main.tsx` never needs to unregister).
 */
export function installPreloadErrorRecovery(opts: PreloadRecoveryOptions = {}): () => void {
  const reload = opts.reload ?? (() => window.location.reload())
  const now = opts.now ?? (() => Date.now())
  const cooldownMs = opts.cooldownMs ?? RELOAD_COOLDOWN_MS

  const handler = (event: Event) => {
    const lastReloadAt = readLastReloadAt()
    // Storage unavailable — we can't prove another reload wouldn't loop,
    // so leave Vite's default behavior (the error boundary) in place.
    if (lastReloadAt === null) return

    if (!shouldAutoReload(now(), lastReloadAt, cooldownMs)) {
      // We reloaded moments ago and it failed again → the deploy is likely
      // genuinely broken. Let Vite throw so the real error surfaces.
      return
    }

    const stampedAt = now()
    if (!writeLastReloadAt(stampedAt)) return // couldn't persist the guard; don't risk a loop

    // We are handling it — stop Vite from also throwing this occurrence.
    event.preventDefault()

    const payload = (event as VitePreloadErrorEvent).payload
    const failedUrl = payload instanceof Error ? payload.message : undefined

    // Record one durable message (not a breadcrumb — a breadcrumb only
    // ships if a *later* error is captured, and we're about to reload, so
    // there is no "later"). Flush before reload so the transport isn't
    // torn down mid-send; the flush timeout bounds the wait, and no-op
    // mode resolves immediately.
    const reporter = getErrorReporter()
    reporter.captureMessage(
      'Reloading once to recover from stale deployment assets (vite:preloadError).',
      'warn',
      { tags: { source: 'preloadRecovery' }, extra: { failedUrl } },
    )
    void reporter.flush(1000).finally(() => {
      reload()
    })
  }

  window.addEventListener('vite:preloadError', handler)
  return () => window.removeEventListener('vite:preloadError', handler)
}
